import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Fade,
  Container,
  Stack,
  createTheme,
  ThemeProvider,
  alpha,
  useTheme,
  useMediaQuery,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import {
  CalendarToday,
  EventAvailable,
  EventBusy,
  FiberManualRecord,
  AccessTime,
  Edit,
  Delete,
  ContentCopy,
  CheckCircle,
  Close,
  Add
} from '@mui/icons-material';
import {
  useGetJobServiceQuery,
  useCreatePostApiMutation,
  useGetAcceptedGeneratedPostsAllQuery,
  useAmountToPayMutation,
  useDeleteUnpaidBookingMutation
} from '../../../features/OverViewAuth/overViewApi';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppDispatch } from '../../../app/store';
import { setBookingDetails } from '../../../features/PostCreat/postcreateSlice';
import { createLocalDateTime, formatToLocalWithOffset, getLocalDateString, getLocalTimeString } from '../../../utils/LocalDate';
import { checkBookingConflict } from '../../../utils/bookingValidation';

// Sub-components
import CalendarHeader from './components/CalendarHeader';
import CalendarGrid from './components/CalendarGrid';
import DuplicateModal from './components/DuplicateModal';
import ServiceDateSelection from './components/ServiceDateSelection';

// Interfaces
interface Booking {
  id: number;
  date: string;
  service: string;
  startTime: string;
  endTime: string;
  originalBookingId?: string;
  serviceId?: string;
  _id?: string; // Backend ID

  // Store ALL booking details directly on the object
  location?: string;
  ageRange?: number[];
  description?: string;
  personRequired?: number;
  language?: string[];
  extraDetails?: any;
  attachment?: any[];
  hours?: number;
  timeUpdate?: string;
  updateEndTime?: string;
  postPrice?: number;
  tax?: number;

  // Store the original data for reference (optional)
  originalDetails?: any;

  // Flag to indicate this is a read-only backend booking
  isReadOnly?: boolean;
  allowEditDelete?: boolean;

  // Timezone-aware absolute timestamps
  from?: string;
  till?: string;
}

// Interface for existing bookings from backend
interface ExistingBooking {
  _id: string;
  from: string;
  till: string;
  service: {
    _id: string;
    name: string;
    price: number;
  };
  status: string;
  ispaymentVeified: boolean;
  activeJobflow?: string;
  location: string;
  description: string;
  personRequired: number;
  language: string[];
  ageRange: number[];
  extraDetails?: string;
  isDraft?: boolean;
  isConfirm?: boolean;
}

// Custom Pink Theme
const pinkTheme = createTheme({
  palette: {
    primary: {
      main: '#F475A4',
      light: '#FF9AC4',
      dark: '#D45E8C',
      contrastText: '#fff',
    },
    secondary: {
      main: '#FF6B9D',
      light: '#FFB3C6',
      dark: '#C74375',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(244, 117, 164, 0.08)',
    '0px 4px 8px rgba(244, 117, 164, 0.12)',
    '0px 6px 16px rgba(244, 117, 164, 0.16)',
    '0px 8px 24px rgba(244, 117, 164, 0.20)',
    '0px 12px 32px rgba(244, 117, 164, 0.24)',
    '0px 16px 40px rgba(244, 117, 164, 0.28)',
    '0px 20px 48px rgba(244, 117, 164, 0.32)',
    '0px 24px 56px rgba(244, 117, 164, 0.36)',
    ...Array(16).fill('0px 24px 56px rgba(244, 117, 164, 0.36)'),
  ] as any,
});

export default function BookingCalendar() {
  const [amountToPay, { isLoading: isCalculatingAmount }] = useAmountToPayMutation();
  const [deleteUnpaidBooking] = useDeleteUnpaidBookingMutation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State
  const [bookings, setBookings] = useState<Booking[]>(() => {
    // Load from session storage on initialization
    const saved = sessionStorage.getItem('booking_calendar_items');
    const items: Booking[] = saved ? JSON.parse(saved) : [];

    // Re-verify local times based on absolute timestamps in case timezone changed
    return items.map(item => {
      if (item.from && item.till) {
        const fromDate = new Date(item.from);
        const tillDate = new Date(item.till);
        return {
          ...item,
          date: getLocalDateString(fromDate),
          startTime: getLocalTimeString(fromDate),
          endTime: getLocalTimeString(tillDate)
        };
      }
      return item;
    });
  });
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [isDuplicatingLocal, setIsDuplicatingLocal] = useState(false);

  // Modal State
  const [bookingModalOpen, setBookingModalOpen] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);

  // Duplicate Modal State (for manual copy action)
  const [duplicateModalOpen, setDuplicateModalOpen] = useState<boolean>(false);
  const [selectedBookingForDuplicate, setSelectedBookingForDuplicate] = useState<Booking | null>(null);

  // Source booking for "Quick Add" (+) feature
  // Initialize from session storage
  const [sourceBookingId, setSourceBookingId] = useState<string | number | null>(() => {
    const saved = sessionStorage.getItem('booking_source_id');
    return saved ? JSON.parse(saved) : null;
  });
  const [sourceBooking, setSourceBooking] = useState<Booking | null>(null);
  const [baseBookingDetails, setBaseBookingDetails] = useState<any>(null);

  // New Booking Flow State
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [dateTimeModalOpen, setDateTimeModalOpen] = useState(false);
  const [selectedServiceForFlow, setSelectedServiceForFlow] = useState<{ id: string, name: string } | null>(null);
  const [flowDate, setFlowDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [flowStartTime, setFlowStartTime] = useState<string>('09:00');
  const [flowEndTime, setFlowEndTime] = useState<string>('10:00');

  // Determine view based on path
  // Strip any trailing slash so external entry URLs like
  // "/dashboard/service-selection/" (some hosts append the slash) still match.
  const normalizedPathname = location.pathname.replace(/\/+$/, '');
  const isServiceSelectionRoute = normalizedPathname === '/dashboard/service-selection';
  const isCalendarOnlyRoute = normalizedPathname === '/dashboard/calendar';

  // Initial Setup Screen State
  const [showInitialSetup, setShowInitialSetup] = useState(() => {
    // If on calendar-only route, ALWAYS skip setup and show calendar grid
    if (isCalendarOnlyRoute) return false;

    // If on service selection route, ALWAYS show it
    if (isServiceSelectionRoute) return true;

    // Check session storage first (for refreshes/tab switching)
    const saved = sessionStorage.getItem('booking_calendar_items');
    const hasUserBookings = saved ? JSON.parse(saved).length > 0 : false;

    // Check location state (for incoming data from other pages)
    const incomingData = location.state?.bookingDetails || location.state?.range || location.state?.dateRange;

    // If we have either, skip initial setup immediately to prevent UX flicker
    return !(hasUserBookings || incomingData);
  });
  const [isAddingNewService, setIsAddingNewService] = useState(false);

  // Sync state with route
  useEffect(() => {
    if (isServiceSelectionRoute) {
      setShowInitialSetup(true);
    }
    if (isCalendarOnlyRoute) {
      setShowInitialSetup(false);
    }
  }, [location.pathname, isServiceSelectionRoute, isCalendarOnlyRoute]);

  // Check if we should skip setup (e.g. editing or existing bookings)
  useEffect(() => {
    if (isServiceSelectionRoute) return;

    const hasUserBookings = bookings.length > 0;

    if (location.state?.bookingDetails || location.state?.range || location.state?.dateRange || hasUserBookings) {
      setShowInitialSetup(false);
    }
    else {
      setShowInitialSetup(true);
    }
  }, [location.state, location.key, bookings, isServiceSelectionRoute]);

  const handleSelectionComplete = (data: { serviceId: string; serviceName: string; fromDate: string; toDate: string; fromTime: string; toTime: string; subModality?: string; cleaningType?: string; supplies?: string }) => {
    // Redirect to Booking Form with range data in search params to persist on refresh
    const queryParams = new URLSearchParams({
      serviceId: data.serviceId,
      serviceName: data.serviceName,
      fromDate: data.fromDate,
      toDate: data.toDate,
      fromTime: data.fromTime,
      toTime: data.toTime
    });
    if (data.subModality) {
      queryParams.set('subModality', data.subModality);
    }
    if (data.cleaningType) {
      queryParams.set('cleaningType', data.cleaningType);
    }
    if (data.supplies) {
      queryParams.set('supplies', data.supplies);
    }
    navigate(`/dashboard/booking-form/${data.serviceId}?${queryParams}`, {
      state: {
        range: data,
        from: 'calendar_initial_setup'
      }
    });
  };

  // Queries & Mutations
  const { data: getservice } = useGetJobServiceQuery({});
  const { data: existingBookingsData, isLoading: isLoadingExistingBookings } = useGetAcceptedGeneratedPostsAllQuery({});
  const [createPost, { isLoading: isDuplicating }] = useCreatePostApiMutation();
  // Save bookings and source booking ID to session storage
  useEffect(() => {
    sessionStorage.setItem('booking_calendar_items', JSON.stringify(bookings));
    if (sourceBookingId) {
      sessionStorage.setItem('booking_source_id', JSON.stringify(sourceBookingId));
    } else {
      sessionStorage.removeItem('booking_source_id');
    }
  }, [bookings, sourceBookingId]);

  // Initialize from Location State (returning from Edit Page after submission)
  useEffect(() => {
    if (location.state?.bookingDetails) {
      const details = location.state.bookingDetails;
      // If we have a range, we need to generate bookings for each day
      // Note: CreateBooking passes 'dateRange' property in state, but logic above used 'range' property from initial setup?
      // Let's check CreateBooking again. It sets: state: { dateRange: dateRange, ... }
      // The previous replace_file_content (Step 69) shows: state: { bookingDetails: payload, dateRange: dateRange, ... }
      // So we should check location.state.dateRange

      const dateRangeData = location.state?.dateRange || location.state?.range;
      const bookingsCreated = location.state?.bookingsCreated;
      const createdBookingsFromState = location.state?.createdBookings;

      if (dateRangeData) {

        // If bookings were already created by EditBookingPage and passed back
        if (bookingsCreated && createdBookingsFromState && Array.isArray(createdBookingsFromState)) {
          const [fYear, fMonth, fDay] = dateRangeData.fromDate.split('-').map(Number);
          const start = new Date(fYear, fMonth - 1, fDay);

          // Map backend objects to frontend Booking interface
          const newMappedBookings: Booking[] = createdBookingsFromState.map((item: any) => {
            const fromDate = item.from ? new Date(item.from) : new Date();
            const tillDate = item.till ? new Date(item.till) : new Date();
            const itemDate = getLocalDateString(fromDate);

            return {
              id: item._id || Date.now() + Math.random(),
              _id: item._id, // IMPORTANT: Backend ID
              date: itemDate,
              service: item.serviceOrName || item.serviceName || details.serviceName || details.service,
              serviceId: item.serviceId || item.service,
              startTime: getLocalTimeString(fromDate),
              endTime: getLocalTimeString(tillDate),

              // Copy details
              location: details.location,
              ageRange: details.ageRange,
              description: details.description,
              personRequired: details.personRequired,
              language: details.language,
              extraDetails: details.extraDetails,
              attachment: details.attachment,
              hours: details.hours,
              timeUpdate: getLocalTimeString(fromDate),
              updateEndTime: getLocalTimeString(tillDate),

              from: item.from,
              till: item.till,

              isReadOnly: false, // Make them editable/selectable for payment
              allowEditDelete: true,

              originalBookingId: item._id,
              originalDetails: {
                status: 'Generated', // or Draft
                ispaymentVeified: false
              }
            };
          });

          setBookings(prev => {
            // De-duplicate just in case
            const existingIds = new Set(prev.map(b => b._id).filter(Boolean));
            const uniqueNew = newMappedBookings.filter(b => !existingIds.has(b._id));
            return [...prev, ...uniqueNew];
          });

          // Set source for duplication
          if (newMappedBookings.length > 0) {
            setBaseBookingDetails(details);
            setSourceBooking(newMappedBookings[0]);
            setSourceBookingId(newMappedBookings[0]._id || null);
          }

          setCurrentMonth(start);
          window.history.replaceState({}, document.title);
          return;
        }

        // Optimistic generation (fallback if not created on backend yet, or old flow)
        if (bookingsCreated && !createdBookingsFromState) {
          const start = new Date(dateRangeData.fromDate);
          setCurrentMonth(start);
          window.history.replaceState({}, document.title);
          return;
        }

        const generatedBookings: Booking[] = [];
        const [fYear, fMonth, fDay] = dateRangeData.fromDate.split('-').map(Number);
        const [tYear, tMonth, tDay] = dateRangeData.toDate.split('-').map(Number);
        const start = new Date(fYear, fMonth - 1, fDay);
        const end = new Date(tYear, tMonth - 1, tDay);
        const loop = new Date(start);

        // Calculate hours from times if needed
        // details.hours should already be set from form

        while (loop <= end) {
          const dateStr = getLocalDateString(loop);

          // Create a booking for this day based on the form details
          const newBooking: Booking = {
            id: Date.now() + Math.random(),
            // No backend _id yet
            date: dateStr,
            service: details.serviceName || details.service,
            serviceId: details.serviceId || details.service,
            startTime: dateRangeData.fromTime || details.timeUpdate || '09:00',
            endTime: dateRangeData.toTime || details.updateEndTime || '10:00',

            // Copy all details
            location: details.location,
            ageRange: details.ageRange,
            description: details.description,
            personRequired: details.personRequired,
            language: details.language,
            extraDetails: details.extraDetails,
            attachment: details.attachment,
            hours: details.hours,
            timeUpdate: details.timeUpdate,
            updateEndTime: details.updateEndTime,
            isReadOnly: false,

            // Set absolute timestamps for timezone awareness
            from: formatToLocalWithOffset(createLocalDateTime(dateStr, dateRangeData.fromTime || details.timeUpdate || '09:00')),
            till: formatToLocalWithOffset(createLocalDateTime(dateStr, dateRangeData.toTime || details.updateEndTime || '10:00')),
          };

          generatedBookings.push(newBooking);
          loop.setDate(loop.getDate() + 1);
        }

        setBookings(prev => {
          // Append new bookings
          return [...prev, ...generatedBookings];
        });

        // Set source booking from the first one for future duplications
        const firstBooking = generatedBookings[0];
        setBaseBookingDetails(details);
        setSourceBooking(firstBooking);

        // Navigate calendar to start
        setCurrentMonth(start);

        // Clear location state
        window.history.replaceState({}, document.title);
        return;
      }

      // Fallback for single booking updates (old flow)
      const originalBookingId = location.state.originalBookingId || details._id;
      const bookingId = details._id || details.id || Date.now();

      const initialBooking: Booking = {
        id: bookingId,
        _id: details._id,
        date: details.from ? getLocalDateString(new Date(details.from)) : details.date || getLocalDateString(new Date()),
        service: details.serviceName || details.service,
        startTime: details.timeUpdate || details.startTime,
        endTime: details.updateEndTime || details.endTime,
        originalBookingId: originalBookingId,
        serviceId: details.serviceId || details.service,

        // Store details specific to this booking
        location: details.location,
        ageRange: details.ageRange,
        description: details.description,
        personRequired: details.personRequired,
        language: details.language,
        extraDetails: details.extraDetails,
        attachment: details.attachment,
        hours: details.hours,
        timeUpdate: details.timeUpdate,
        updateEndTime: details.updateEndTime,
        isReadOnly: false,
        from: details.from,
        till: details.till
      };

      setBookings(prev => {
        // Try to find the booking to update
        const existsIndex = prev.findIndex(b => {
          // Match by backend _id
          if (b._id && details._id && b._id === details._id) return true;
          // Match by originalBookingId (for edits)
          if (b.originalBookingId && originalBookingId && b.originalBookingId === originalBookingId) return true;
          // Match by id (fallback)
          if (b.id === bookingId) return true;
          return false;
        });

        if (existsIndex >= 0) {
          // This is an EDIT - update only this specific booking
          console.log("Updating existing booking at index", existsIndex, "with ID", details._id);
          const newBookings = [...prev];
          newBookings[existsIndex] = initialBooking;

          setSourceBooking(initialBooking);
          setBaseBookingDetails(details);
          if (!sourceBookingId) {
            setSourceBookingId(bookingId);
          }

          return newBookings;
        }

        setBaseBookingDetails(details);
        setSourceBooking(initialBooking);
        setSourceBookingId(bookingId);

        console.log("Adding new booking with ID", details._id);
        return [...prev, initialBooking];
      });

      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const backendBookingsLoadedRef = React.useRef(false);

  useEffect(() => {
    if (existingBookingsData?.data && Array.isArray(existingBookingsData.data) && !backendBookingsLoadedRef.current) {
      const confirmedBackendBookings: Booking[] = existingBookingsData.data
        .map((item: ExistingBooking) => {
          const fromDate = new Date(item.from);
          const tillDate = new Date(item.till);

          const dateStr = getLocalDateString(fromDate);

          const bookingVisualDate = new Date(dateStr + 'T00:00:00'); // Local midnight for logic

          const today = new Date();
          today.setHours(0, 0, 0, 0); // Local midnight today

          const isFutureOrToday = bookingVisualDate >= today;

          const isDraft = item.isDraft === true;
          const isPaymentPending = item.isConfirm === true && item.isDraft === true && item.ispaymentVeified === false;
          const isExpiredUnpaid = !isFutureOrToday && !item.ispaymentVeified;

          let isReadOnly = true;
          let allowEditDelete = false;
          let displayStatus = item.status;

          if (isExpiredUnpaid) {
            // Case 1: Past & Unpaid -> Totally disabled/Expired
            isReadOnly = true;
            allowEditDelete = false;
            displayStatus = 'Expired'; // Force status to Expired to trigger grey color
          } else if (isDraft || isPaymentPending || (displayStatus === 'Generated' && !item.ispaymentVeified)) {
            // Case 2: Draft, Payment Pending, or Generated (Unpaid)
            isReadOnly = false;
            // ENABLE DELETE FOR PAYMENT PENDING
            allowEditDelete = true;
            displayStatus = isPaymentPending ? 'Payment Pending' : item.status;
          } else {
            // Case 3: Confirmed / Others
            isReadOnly = true;
            allowEditDelete = false;
          }

          return {
            id: item._id,
            _id: item._id,
            date: dateStr,
            service: item.service.name,
            serviceId: item.service._id,
            startTime: getLocalTimeString(fromDate),
            endTime: getLocalTimeString(tillDate),
            location: item.location,
            ageRange: item.ageRange,
            description: item.description,
            personRequired: item.personRequired,
            language: item.language,
            extraDetails: item.extraDetails ? (typeof item.extraDetails === 'string' ? JSON.parse(item.extraDetails) : item.extraDetails) : undefined,
            from: item.from, // Store absolute timestamp
            till: item.till, // Store absolute timestamp
            originalBookingId: item._id,
            isReadOnly: isReadOnly,
            allowEditDelete: allowEditDelete,
            postPrice: item.service.price,
            originalDetails: {
              ispaymentVeified: item.ispaymentVeified,
              activeJobflow: item.activeJobflow,
              status: displayStatus
            }
          };
        });

      setBookings(prev => {
        const newBackendIds = new Set(confirmedBackendBookings.map(b => b._id));

        const localBookings = prev.filter(b => {
          if (b._id && newBackendIds.has(b._id)) return false;

          if (b.isReadOnly) return false;

          return true;
        });

        const mergedBookings = [...confirmedBackendBookings, ...localBookings];
        return mergedBookings;
      });

      // Mark as loaded
      backendBookingsLoadedRef.current = true;
    }
  }, [existingBookingsData]);

  useEffect(() => {
    if (isLoadingExistingBookings) {
      return;
    }

    if (sourceBookingId && bookings.length > 0) {
      const foundSourceBooking = bookings.find(
        b => (b._id === sourceBookingId || b.id === sourceBookingId) && !b.isReadOnly
      );

      if (foundSourceBooking) {
        setSourceBooking(foundSourceBooking);
        setBaseBookingDetails({
          location: foundSourceBooking.location,
          service: foundSourceBooking.serviceId || foundSourceBooking.service,
          serviceName: foundSourceBooking.service,
          ageRange: foundSourceBooking.ageRange,
          description: foundSourceBooking.description,
          personRequired: foundSourceBooking.personRequired,
          language: foundSourceBooking.language,
          extraDetails: foundSourceBooking.extraDetails,
          attachment: foundSourceBooking.attachment,
          timeUpdate: foundSourceBooking.startTime,
          hours: foundSourceBooking.hours || 1
        });
      } else {
        const hasUserBookings = bookings.some(b => !b.isReadOnly);
        if (hasUserBookings) {
          setSourceBooking(null);
          setSourceBookingId(null);
          setBaseBookingDetails(null);
        }
      }
    } else if (!sourceBookingId && sourceBooking) {
      setSourceBooking(null);
      setBaseBookingDetails(null);
    }
  }, [bookings, sourceBookingId, isLoadingExistingBookings]);


  const openBookingModal = (date: string, booking?: Booking) => {
    setSelectedDate(date);
    setEditingBooking(booking || null);
    setBookingModalOpen(true);
  };

  // Handle booking deletion
  const handleDeleteBooking = async (id: number | string) => {
    const bookingToDelete = bookings.find(b => b.id === id || b._id === id);

    // Call API if it exists in the database (has a _id)
    if (bookingToDelete?._id) {
      try {
        await deleteUnpaidBooking({ bookingId: bookingToDelete._id }).unwrap();
        toast.success("Booking deleted successfully");
        // State update handled below or via invalidation
      } catch (error) {
        toast.error("Failed to delete booking");
        console.error(error);
        return; // Stop local deletion if API fails
      }
    }

    // Check if we're deleting the source booking BEFORE updating state
    const isDeletingSourceBooking = bookingToDelete && sourceBookingId &&
      (bookingToDelete.id === sourceBookingId || bookingToDelete._id === sourceBookingId);

    // Calculate remaining bookings
    const remainingBookings = bookings.filter(b => b.id !== id && b._id !== id);
    const remainingUserBookings = remainingBookings.filter(b => !b.isReadOnly);

    // Update bookings state
    setBookings(remainingBookings);

    // Handle source booking logic
    if (remainingUserBookings.length === 0 || isDeletingSourceBooking) {
      setSourceBooking(null);
      setSourceBookingId(null);
      setBaseBookingDetails(null);

      if (remainingUserBookings.length === 0) {
        navigate('/dashboard/create-booking', { replace: true, state: {} });
        setShowInitialSetup(true);
      }
    }
  };
  const handleSaveBooking = (data: { serviceId: string; serviceName: string; startTime: string; endTime: string }) => {
    if (editingBooking) {
      // Update local state
      setBookings(bookings.map(b =>
        b.id === editingBooking.id
          ? { ...b, service: data.serviceName, startTime: data.startTime, endTime: data.endTime, serviceId: data.serviceId }
          : b
      ));
    } else {
    }
    setBookingModalOpen(false);

    // Navigate to form for new bookings (not edits)
    if (!editingBooking) {
      navigate(`/dashboard/booking-form/${data.serviceId}?serviceId=${data.serviceId}&serviceName=${encodeURIComponent(data.serviceName)}&date=${selectedDate}`, {
        state: {
          bookingData: {
            service: data.serviceId,
            serviceName: data.serviceName,
            date: selectedDate,
            startTime: data.startTime,
            endTime: data.endTime
          },
          from: 'calendar_new' // Indicate this is a new booking
        }
      });
    }
  };

  const handleDuplicateBooking = (booking: Booking) => { // Open Modal
    setSelectedBookingForDuplicate(booking);
    setDuplicateModalOpen(true);
  };

  const createDuplicatePayload = (date: string, booking: Booking, details?: any): FormData | null => {
    // Use provided details if available, otherwise use booking fields
    const bookingDetails = details || {
      location: booking.location,
      service: booking.serviceId || booking.service,
      serviceName: booking.service,
      ageRange: booking.ageRange,
      from: booking.from || (booking.date && booking.startTime ? formatToLocalWithOffset(new Date(`${booking.date}T${booking.startTime}:00`)) : ''),
      till: booking.till || (booking.date && booking.endTime ? formatToLocalWithOffset(new Date(`${booking.date}T${booking.endTime}:00`)) : ''),
      description: booking.description,
      personRequired: booking.personRequired,
      language: booking.language,
      extraDetails: booking.extraDetails,
      attachment: booking.attachment,
      timeUpdate: booking.startTime,
      hours: booking.hours || 1
    };

    if (!bookingDetails) return null;

    // Calculate dates for the NEW duplicate booking
    // Calculate dates for the NEW duplicate booking 
    // We want the same LOCAL time on the NEW date
    const startTimeStr = bookingDetails.timeUpdate || booking.startTime;
    const newStartDate = createLocalDateTime(date, startTimeStr);
    const durationHours = Number(bookingDetails.hours) || 1;
    const newEndDate = new Date(newStartDate);
    newEndDate.setTime(newStartDate.getTime() + durationHours * 60 * 60 * 1000);

    // Format new dates using the helper
    const newFromISO = formatToLocalWithOffset(newStartDate);
    const newTillISO = formatToLocalWithOffset(newEndDate);

    const payload = new FormData();
    payload.append("location", bookingDetails.location);
    payload.append("service", bookingDetails.service);
    payload.append("serviceName", bookingDetails.serviceName);
    payload.append("ageRange", bookingDetails.ageRange?.toString());
    payload.append("from", newFromISO);
    payload.append("till", newTillISO);
    payload.append("description", bookingDetails.description);
    payload.append("personRequired", bookingDetails.personRequired?.toString());
    payload.append("language", bookingDetails.language);

    if (bookingDetails.attachment && Array.isArray(bookingDetails.attachment)) {
      bookingDetails.attachment.forEach((image: any, index: number) => {
        const key = index === 0 ? "attachment" : `attachment${index}`;
        payload.append(key, image);
      });
    }

    if (bookingDetails.extraDetails) {
      payload.append("extraDetails", JSON.stringify(bookingDetails.extraDetails));
    }

    payload.append("hours", bookingDetails.hours?.toString() || "1");

    return payload;
  };

  const confirmDuplicate = async (fromDate: string, toDate: string, fromTime: string, toTime: string) => {
    if (!selectedBookingForDuplicate || !fromDate || !toDate) return;
    setIsDuplicatingLocal(true);

    const [fYear, fMonth, fDay] = fromDate.split('-').map(Number);
    const [tYear, tMonth, tDay] = toDate.split('-').map(Number);
    const start = new Date(fYear, fMonth - 1, fDay);
    const end = new Date(tYear, tMonth - 1, tDay);
    const loop = new Date(start);
    const promises: any[] = [];
    const datesAttempts: string[] = [];

    while (loop <= end) {
      const dateStr = getLocalDateString(loop);
      datesAttempts.push(dateStr);

      const isSameSlot = (dateStr === selectedBookingForDuplicate.date) && (fromTime === selectedBookingForDuplicate.startTime);

      const startTimeStr = fromTime;
      const isoFrom = formatToLocalWithOffset(createLocalDateTime(dateStr, startTimeStr));

      const hasConflict = checkBookingConflict(
        {
          serviceId: selectedBookingForDuplicate.serviceId || selectedBookingForDuplicate.service,
          from: isoFrom,
        },
        existingBookingsData?.data || []
      );

      // We skip duplication if it's the exact same date+time or if a booking already exists
      if (!isSameSlot && !hasConflict) {
        const payload = createDuplicatePayload(dateStr, selectedBookingForDuplicate, {
          location: selectedBookingForDuplicate.location,
          service: selectedBookingForDuplicate.serviceId || selectedBookingForDuplicate.service,
          serviceName: selectedBookingForDuplicate.service,
          ageRange: selectedBookingForDuplicate.ageRange,
          description: selectedBookingForDuplicate.description,
          personRequired: selectedBookingForDuplicate.personRequired,
          language: selectedBookingForDuplicate.language,
          extraDetails: selectedBookingForDuplicate.extraDetails,
          attachment: selectedBookingForDuplicate.attachment,
          timeUpdate: fromTime,
          hours: selectedBookingForDuplicate.hours || 1
        });

        if (payload) {
          const promise = createPost(payload).then(res => ({ res, date: dateStr }));
          promises.push(promise);
        }
      }

      loop.setDate(loop.getDate() + 1);
    }

    if (promises.length === 0) {
      toast.warning("No valid slots found for duplication (slots may overlap or already have bookings).");
      setIsDuplicatingLocal(false);
      return;
    }

    try {
      const results = await Promise.all(promises);
      let successCount = 0;
      const newBookingsFromApi: Booking[] = [];

      results.forEach(({ res, date }) => {
        if (res?.data?.status === 200) {
          successCount++;
          const createdPost = res.data.data.createdPost;

          const fromDate = createdPost.from ? new Date(createdPost.from) : new Date();
          const tillDate = createdPost.till ? new Date(createdPost.till) : new Date();

          const newBooking: Booking = {
            id: createdPost._id || Date.now() + Math.random(),
            _id: createdPost._id,
            date: getLocalDateString(fromDate),
            service: selectedBookingForDuplicate.service,
            startTime: getLocalTimeString(fromDate),
            endTime: getLocalTimeString(tillDate),
            serviceId: selectedBookingForDuplicate.serviceId,
            originalBookingId: createdPost._id,

            from: createdPost.from,
            till: createdPost.till,

            // Copy details
            location: selectedBookingForDuplicate.location,
            ageRange: selectedBookingForDuplicate.ageRange,
            description: selectedBookingForDuplicate.description,
            personRequired: selectedBookingForDuplicate.personRequired,
            language: selectedBookingForDuplicate.language,
            extraDetails: selectedBookingForDuplicate.extraDetails,
            attachment: selectedBookingForDuplicate.attachment,
            hours: selectedBookingForDuplicate.hours,
            isReadOnly: false,
            allowEditDelete: true,
            originalDetails: {
              status: 'Generated',
              ispaymentVeified: false
            }
          };
          newBookingsFromApi.push(newBooking);
        }
      });

      if (successCount > 0) {
        setBookings(prev => [...prev, ...newBookingsFromApi]);
        toast.success(`Successfully duplicated ${successCount} bookings!`);
      } else {
        toast.error("Failed to duplicate bookings.");
      }

    } catch (err: any) {
      toast.error(err?.message || "Failed to duplicate booking");
    } finally {
      setIsDuplicatingLocal(false);
      setDuplicateModalOpen(false);
      setSelectedBookingForDuplicate(null);
    }
  };

  const handleQuickDuplicate = async (date: string) => {
    if (!sourceBooking) {
      toast.warning("No source booking available. Please create a booking first.");
      return;
    }

    // Check if a booking already exists for this slot using centralized validation
    const isoFrom = formatToLocalWithOffset(createLocalDateTime(date, sourceBooking.startTime));
    const hasConflict = checkBookingConflict(
      {
        serviceId: sourceBooking.serviceId || sourceBooking.service,
        from: isoFrom,
      },
      existingBookingsData?.data || []
    );

    if (hasConflict) {
      toast.warning("A booking for this service already exists on this date and time.");
      return;
    }

    setIsDuplicatingLocal(true);
    try {
      const payload = createDuplicatePayload(date, sourceBooking, baseBookingDetails);
      if (!payload) throw new Error("Could not construct payload");

      const res: any = await createPost(payload);

      if (res?.data?.status === 200) {
        const createdPost = res.data.data.createdPost;

        const fromDate = createdPost.from ? new Date(createdPost.from) : new Date();
        const tillDate = createdPost.till ? new Date(createdPost.till) : new Date();

        const newBooking: Booking = {
          id: createdPost._id || Date.now(),
          _id: createdPost._id,
          date: getLocalDateString(fromDate),
          service: sourceBooking.service,
          startTime: getLocalTimeString(fromDate),
          endTime: getLocalTimeString(tillDate),
          serviceId: sourceBooking.serviceId,
          originalBookingId: createdPost._id,

          from: createdPost.from,
          till: createdPost.till,

          location: sourceBooking.location,
          ageRange: sourceBooking.ageRange,
          description: sourceBooking.description,
          personRequired: sourceBooking.personRequired,
          language: sourceBooking.language,
          extraDetails: sourceBooking.extraDetails,
          attachment: sourceBooking.attachment,
          hours: sourceBooking.hours
        };

        setBookings(prev => [...prev, newBooking]);
        toast.success("Booking duplicated!");
      } else {
        toast.error(res?.error?.data?.message || "Failed to duplicate booking");
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to duplicate booking");
    } finally {
      setIsDuplicatingLocal(false);
    }
  };

  const handleEditRedirect = (booking: Booking) => {
    // Validate date and time format
    const isValidDate = (dateStr: string) => {
      return /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
    };

    const isValidTime = (timeStr: string) => {
      return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeStr);
    };

    const fromDateObj = booking.from ? new Date(booking.from) : (booking.date && booking.startTime ? new Date(`${booking.date}T${booking.startTime}:00`) : null);
    const tillDateObj = booking.till ? new Date(booking.till) : (booking.date && booking.endTime ? new Date(`${booking.date}T${booking.endTime}:00`) : null);

    const safeFrom = fromDateObj ? formatToLocalWithOffset(fromDateObj) : '';
    const safeTill = tillDateObj ? formatToLocalWithOffset(tillDateObj) : '';

    const safeServiceId = booking.serviceId || "";
    const safeServiceName = booking.service || "";
    const safeDate = booking.date || getLocalDateString(new Date());

    navigate(`/dashboard/booking-form/${safeServiceId}?serviceId=${safeServiceId}&serviceName=${encodeURIComponent(safeServiceName)}&date=${safeDate}`, {
      state: {
        bookingData: {
          _id: booking._id || booking.originalBookingId,
          serviceId: booking.serviceId,
          serviceName: booking.service,
          service: booking.serviceId || booking.service,
          location: booking.location,
          ageRange: booking.ageRange,
          description: booking.description,
          personRequired: booking.personRequired,
          language: booking.language,
          extraDetails: booking.extraDetails,
          attachment: booking.attachment,
          hours: booking.hours,

          // Time fields
          date: booking.date,
          startTime: booking.startTime,
          endTime: booking.endTime,
          timeUpdate: booking.startTime,
          updateEndTime: booking.endTime,
          from: safeFrom,
          till: safeTill,
        },
        from: 'calendar_edit',
        originalBookingId: booking._id || booking.originalBookingId
      }
    });
  };

  const handleDuplicateRedirect = (booking: Booking) => {
    const base = baseBookingDetails || {};

    const bookingData = {
      ...base,
      // Explicitly remove IDs for new booking creation
      _id: undefined,
      id: undefined,

      serviceId: booking.serviceId || base.serviceId,
      serviceName: booking.service || base.serviceName,
      service: booking.service || base.service,

      date: booking.date,
      startTime: booking.startTime,
      endTime: booking.endTime,
      timeUpdate: booking.startTime,
      updateEndTime: booking.endTime,

      from: booking.from || (booking.date && booking.startTime ? formatToLocalWithOffset(new Date(`${booking.date}T${booking.startTime}:00`)) : base.from),
      till: booking.till || (booking.date && booking.endTime ? formatToLocalWithOffset(new Date(`${booking.date}T${booking.endTime}:00`)) : base.till),
    };

    navigate(`/dashboard/booking-form/${booking.serviceId}?serviceId=${booking.serviceId}&serviceName=${encodeURIComponent(booking.service)}&date=${booking.date}`, {
      state: {
        bookingData: bookingData,
        from: 'calendar_duplicate'
      }
    });
  };

  const handleBookingClick = (booking: Booking) => {
    // Check if expired and unpaid (Guard clause)
    // If status is "Expired" (set by our mapping logic) or explicitly past & unpaid
    if (booking.originalDetails?.status === 'Expired') {
      return;
    }

    if (booking._id) {
      navigate(`/dashboard/bookings/details/${booking._id}`);
    } else {
      handleEditRedirect(booking);
    }
  };

  const handleConfirmAndProceed = async () => {
    // Collect only NEW user-created booking IDs (exclude confirmed/read-only bookings)
    const newBookings = bookings.filter(b => !b.isReadOnly);
    const newBookingIds = newBookings
      .map(b => b._id || b.originalBookingId)
      .filter(Boolean); // Only send valid backend IDs

    if (newBookingIds.length === 0) {
      toast.warning("No new bookings to confirm.");
      return;
    }

    try {
      // Calculate amount to pay via API
      const response = await amountToPay({ bookingIds: newBookingIds }).unwrap();

      if (response && (response.status === 200 || !response.status)) {
        // API might return data directly or wrapped in data property depending on server
        const paymentCalculations = response.data || response;

        // Ensure we populate Redux with booking details including price/tax
        // This fixes the issue where navigating back/forth loses the pricing info in Redux
        // causing BookingOverview to show 0 total amount
        if (newBookings.length > 0) {
          const baseBooking = newBookings[0];

          // Find service to get price/tax from the fetched services list
          // This is more reliable than relying on navigation state
          const serviceDetails = getservice?.data?.find(
            (s: any) => s._id === (baseBooking.serviceId || baseBooking.service)
          );

          const payload: any = {
            _id: baseBooking._id,
            service: baseBooking.serviceId || baseBooking.service,
            serviceName: baseBooking.service,
            location: baseBooking.location,
            ageRange: baseBooking.ageRange?.toString(),
            description: baseBooking.description,
            personRequired: baseBooking.personRequired,
            language: baseBooking.language,
            extraDetails: baseBooking.extraDetails,
            attachment: baseBooking.attachment,
            hours: baseBooking.hours || 1,

            // Set postPrice from paymentCalculations breakdown or fall back to service price
            postPrice: paymentCalculations?.breakdown?.[0]?.pricePerHour
              ?? paymentCalculations?.breakdown?.[0]?.specialPricePerHour
              ?? serviceDetails?.price
              ?? baseBooking.postPrice
              ?? 0,
            tax: paymentCalculations?.taxPercent ?? serviceDetails?.tax ?? 12,

            // Time fields
            timeUpdate: baseBooking.startTime,
            updateEndTime: baseBooking.endTime,
            from: baseBooking.from || (baseBooking.date && baseBooking.startTime ? formatToLocalWithOffset(new Date(`${baseBooking.date}T${baseBooking.startTime}:00`)) : undefined),
            till: baseBooking.till || (baseBooking.date && baseBooking.endTime ? formatToLocalWithOffset(new Date(`${baseBooking.date}T${baseBooking.endTime}:00`)) : undefined),
          };

          dispatch(setBookingDetails(payload));
        }

        // Don't clear session storage here - keep bookings visible when navigating back
        // Session storage will be cleared after successful payment in BookingOverview

        navigate('/dashboard/booking-overview', {
          state: {
            bookingIds: newBookingIds,
            // We still pass this just in case, but Redux is the primary source now
            bookingDetails: location.state?.bookingDetails,
            paymentCalculations: paymentCalculations
          }
        });
      } else {
        toast.error("Failed to calculate payment amount. Please try again.");
      }
    } catch (error: any) {
      console.error("Payment calculation error:", error);
      toast.error(error?.data?.message || "An error occurred while calculating payment.");
    }
  };

  const handleSelectServiceClick = () => {
    setServiceModalOpen(true);
  };

  const handleServiceSelectFlow = (serviceId: string) => {
    const service = getservice?.data?.find((s: any) => s._id === serviceId);
    if (service) {
      setSelectedServiceForFlow({ id: service._id, name: service.name });
      setServiceModalOpen(false);
      setDateTimeModalOpen(true);
    }
  };

  const handleDateTimeSubmit = () => {
    if (!selectedServiceForFlow) return;

    setDateTimeModalOpen(false);

    navigate(`/dashboard/booking-form/${selectedServiceForFlow.id}?serviceId=${selectedServiceForFlow.id}&serviceName=${encodeURIComponent(selectedServiceForFlow.name)}&date=${flowDate}`, {
      state: {
        bookingData: {
          service: selectedServiceForFlow.id,
          serviceName: selectedServiceForFlow.name,
          date: flowDate,
          startTime: flowStartTime,
          endTime: flowEndTime
        },
        from: 'calendar_new'
      }
    });
  };


  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));

  const handleSelectMonthYear = (date: Date) => {
    setCurrentMonth(date);
  };

  return (
    <ThemeProvider theme={pinkTheme}>
      {/* Global Loader Backdrop */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 101,
          backdropFilter: 'blur(4px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
        open={isDuplicating || isDuplicatingLocal || isCalculatingAmount || isLoadingExistingBookings}
      >
        <CircularProgress color="primary" thickness={5} size={60} />
        <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
          {isDuplicatingLocal || isDuplicating ? "Duplicating Bookings..." :
            isCalculatingAmount ? "Calculating Payment..." :
              "Processing..."}
        </Typography>
      </Backdrop>

      <Box sx={{
        py: { xs: 0.5, sm: 1, md: 1 },
        px: 0,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `radial-gradient(circle at 20% 50%, ${alpha('#F475A4', 0.03)} 0%, transparent 50%),
                           radial-gradient(circle at 80% 80%, ${alpha('#FF9AC4', 0.03)} 0%, transparent 50%)`,
          pointerEvents: 'none',
        }
      }}>
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, px: { xs: 0, sm: 2, md: 2 } }}>
          {showInitialSetup || isAddingNewService || isServiceSelectionRoute ? (
            <ServiceDateSelection
              onNext={handleSelectionComplete}
              onBack={() => {
                if (isAddingNewService) setIsAddingNewService(false);
                else navigate(-1);
              }}
            />
          ) : isCalendarOnlyRoute && bookings.length === 0 ? (
            // Calendar-only empty state — show CTA to create first booking
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: { xs: '60vh', sm: 'calc(100vh - 200px)' },
                p: 4,
                borderRadius: 4,
                border: '2px dashed',
                borderColor: alpha('#F475A4', 0.3),
                backgroundColor: alpha('#fff', 0.95),
                backdropFilter: 'blur(20px)',
                boxShadow: '0 12px 40px rgba(244, 117, 164, 0.15)',
                textAlign: 'center',
              }}
            >
              <EventAvailable sx={{ fontSize: 80, color: alpha('#F475A4', 0.4), mb: 2 }} />
              <Typography variant="h5" fontWeight={800} color="#333" mb={1}>
                No Bookings Yet
              </Typography>
              <Typography variant="body1" color="text.secondary" mb={3} maxWidth={400}>
                Start by creating your first booking. Choose a service, pick dates, and we'll handle the rest!
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<Add />}
                onClick={() => navigate('/dashboard/service-selection')}
                sx={{
                  px: 5,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: '1.1rem',
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #F475A4 0%, #FF6B9D 100%)',
                  boxShadow: '0 8px 24px rgba(244, 117, 164, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #FF9AC4 0%, #F475A4 100%)',
                    boxShadow: '0 12px 32px rgba(244, 117, 164, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                }}
              >
                Create New Booking
              </Button>
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: { xs: '80vh', sm: 'calc(100vh - 120px)' },
                overflow: 'hidden',
                p: { xs: 0.5, sm: 2, md: 3 },
                borderRadius: { xs: 3, sm: 4 },
                border: '2px solid',
                borderColor: alpha('#F475A4', 0.15),
                backgroundColor: alpha('#fff', 0.95),
                backdropFilter: 'blur(20px)',
                boxShadow: '0 12px 40px rgba(244, 117, 164, 0.15)',
                position: 'relative',
                pb: { xs: 1, sm: 2, md: 3 }
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  // overflowY: 'auto',
                  pb: 12, // Increased padding so content isn't hidden behind floating button
                }}
              >
                <CalendarHeader
                  currentMonth={currentMonth}
                  onNextMonth={nextMonth}
                  onPrevMonth={prevMonth}
                  onClose={() => navigate(-1)}
                  onSelectMonthYear={handleSelectMonthYear}
                  onAddService={() => navigate('/dashboard/service-selection')}
                />

                {/* Legend for booking colors */}
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    backgroundColor: alpha('#F5F5F5', 0.5),
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: alpha('#E0E0E0', 0.6)
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} mb={1} color="#333">
                    Booking Status:
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 1 }}>

                    {/* Teal - Completed */}
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: 1,
                          background: 'linear-gradient(135deg, #009688 0%, #00796B 100%)'
                        }}
                      />
                      <Typography variant="body2" fontSize="0.8rem">
                        Accepted
                      </Typography>
                    </Box>

                    {/* Green - Accepted */}
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: 1,
                          background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)'
                        }}
                      />
                      <Typography variant="body2" fontSize="0.8rem">
                        Completed
                      </Typography>
                    </Box>

                    {/* Blue - Applied */}
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: 1,
                          background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)'
                        }}
                      />
                      <Typography variant="body2" fontSize="0.8rem">
                        Applied
                      </Typography>
                    </Box>

                    {/* Orange - Generated */}
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: 1,
                          background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)'
                        }}
                      />
                      <Typography variant="body2" fontSize="0.8rem">
                        Generated
                      </Typography>
                    </Box>

                    {/* Grey - Expired*/}
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: 1,
                          background: 'linear-gradient(135deg, #9E9E9E 0%, #616161 100%)'
                        }}
                      />
                      <Typography variant="body2" fontSize="0.8rem">
                        Expired
                      </Typography>
                    </Box>

                    {/* Dark Red - Declined */}
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: 1,
                          background: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)'
                        }}
                      />
                      <Typography variant="body2" fontSize="0.8rem">
                        Declined
                      </Typography>
                    </Box>

                    {/* Purple - Payment Pending */}
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: 1,
                          background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)'
                        }}
                      />
                      <Typography variant="body2" fontSize="0.8rem">
                        Payment Pending
                      </Typography>
                    </Box>

                    {/* Pink - New */}
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: 1,
                          background: 'linear-gradient(135deg, #F475A4 0%, #FF6B9D 100%)'
                        }}
                      />
                      <Typography variant="body2" fontSize="0.8rem">
                        New Booking
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <CalendarGrid
                  currentMonth={currentMonth}
                  bookings={bookings}
                  onDateClick={() => { }}
                  onDeleteBooking={handleDeleteBooking}
                  onDuplicateBooking={handleQuickDuplicate}
                  onEditBooking={handleEditRedirect}
                  onDuplicateBookingItem={handleDuplicateBooking}
                  onBookingClick={handleBookingClick}
                  canDuplicate={!!sourceBooking}
                />
              </Box>
              <Box
                sx={{
                  mt: 0,
                  display: "flex",
                  justifyContent: "end",
                  position: "absolute",
                  bottom: { xs: 20, sm: 30 },
                  right: { xs: 20, sm: 40 },
                  width: 'auto',
                  p: 0,
                  backgroundColor: "transparent",
                  zIndex: 1000,
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<CheckCircle />}
                  onClick={handleConfirmAndProceed}
                  disabled={bookings.filter(b => !b.isReadOnly).length === 0}
                  size="large"
                  sx={{
                    px: { xs: 4, sm: 5 },
                    py: { xs: 1, sm: 1.5 },
                    width: { xs: '90%', sm: 'auto' },
                    textTransform: 'none',
                    fontWeight: 800,
                    fontSize: { xs: '12px', sm: '1.1rem' },
                    borderRadius: 3,
                    color: '#fff',
                    marginRight: { xs: 1, sm: 2 },
                    // Emerald Green Gradient
                    background: 'linear-gradient(135deg, #10B981 0%, #047857 100%)',
                    boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #34D399 0%, #059669 100%)',
                      boxShadow: '0 12px 32px rgba(16, 185, 129, 0.4)',
                      transform: 'translateY(-2px)'
                    },
                    '&:disabled': {
                      background: '#e0e0e0',
                      color: '#9e9e9e',
                      boxShadow: 'none'
                    },
                    animation: bookings.filter(b => !b.isReadOnly).length > 0 ? 'pulseConfirm 2s infinite' : 'none',
                    '@keyframes pulseConfirm': {
                      '0%': { transform: 'scale(1)', boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)' },
                      '50%': { transform: 'scale(1.05)', boxShadow: '0 12px 32px rgba(16, 185, 129, 0.5)' },
                      '100%': { transform: 'scale(1)', boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)' },
                    }
                  }}
                >
                  Confirm Bookings ({bookings.filter(b => !b.isReadOnly).length})
                </Button>
              </Box>
            </Paper>
          )
          }
        </Container >

        {/* <BookingModal
          open={bookingModalOpen}
          onClose={() => setBookingModalOpen(false)}
          selectedDate={selectedDate}
          editingBooking={editingBooking}
          services={getservice?.data || []}
          onSave={handleSaveBooking}
          initialServiceId={searchParams.get("serviceId") || ""}
          initialServiceName={searchParams.get("serviceName") || ""}
          onDuplicate={handleDuplicateBooking}
        /> */}

        <DuplicateModal
          open={duplicateModalOpen}
          onClose={() => setDuplicateModalOpen(false)}
          onConfirm={confirmDuplicate}
          sourceBooking={selectedBookingForDuplicate}
        />

        {/* Service Selection Modal */}
        <Dialog
          open={serviceModalOpen}
          onClose={() => setServiceModalOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, p: 1 }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', fontWeight: 800, color: '#F475A4' }}>
            Select a Service
          </DialogTitle>
          <DialogContent>
            <Box mt={1}>
              <FormControl fullWidth>
                <InputLabel>Service</InputLabel>
                <Select
                  value=""
                  label="Service"
                  onChange={(e) => handleServiceSelectFlow(e.target.value as string)}
                  sx={{ borderRadius: 2 }}
                >
                  {getservice?.data?.map((service: any) => (
                    <MenuItem key={service._id} value={service._id}>
                      {service.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button onClick={() => setServiceModalOpen(false)} sx={{ color: '#888' }}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Date & Time Selection Modal */}
        <Dialog
          open={dateTimeModalOpen}
          onClose={() => setDateTimeModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, p: 2 }
          }}
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #F475A4 0%, #FF6B9D 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CalendarToday sx={{ color: '#fff' }} />
              </Box>
              <Typography variant="h6" fontWeight={800} color="#333">
                Select Date & Time
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              For {selectedServiceForFlow?.name}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} mt={2}>
              <TextField
                label="Date"
                type="date"
                fullWidth
                value={flowDate}
                onChange={(e) => setFlowDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="From"
                  type="time"
                  fullWidth
                  value={flowStartTime}
                  onChange={(e) => setFlowStartTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 2 }
                  }}
                />
                <TextField
                  label="To"
                  type="time"
                  fullWidth
                  value={flowEndTime}
                  onChange={(e) => setFlowEndTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 2 }
                  }}
                />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDateTimeModalOpen(false)} sx={{ color: '#888', fontWeight: 600 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDateTimeSubmit}
              sx={{
                bgcolor: '#4CAF50',
                borderRadius: 2,
                px: 3,
                fontWeight: 700,
                ml: 2,
                '&:hover': { bgcolor: '#43A047' }
              }}
            >
              Next
            </Button>
          </DialogActions>
        </Dialog>
      </Box >
    </ThemeProvider >
  );
}